import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { Button, Checkbox, TextField } from "react-md";
import { MSpinner, MDialog } from "components/Shared";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormControl from "@material-ui/core/FormControl";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import { getMuellerAtHomeStatus, startMuellerAtHome } from "requests/PhoenixIntegration";
import { SEND_MUELLER_HOME_LINK } from "typeConstants/rights";
import { userHasRight } from "selectors/rights";
import { dateToStringYy } from "components/Shared/Formatters";
import { rxEmailAddress, rxPhoneNumber } from "tools/regularExpressions";
import responseHandler from "responseHandler";

// TODO: Add PhoenixIntegration.Services.Models to ts models?

interface MuellerAtHomeStartRequest {
  phoneNumber: string;
  email: string;
  notes: string;
  includeCubiCasaRequest: boolean;
  automaticShareFormIds: string[];
  requestInitiator: string;
  createWebLink: boolean;
}

interface MuellerAtHomeReportDetails {
  status: string;
  createdDate: string;
  completedDate: string;
  startRequest: MuellerAtHomeStartRequest;
  requestCount: number;
  fulfilledRequestCount: number;
  unableToTakeCount: number;
  totalPhotoCount: number;
}

interface MuellerAtHomeForm {
  id: string;
  name: string;
}

interface MuellerAtHomeFullStatus {
  canOpenNewInspection: boolean;
  canOfferCubiCasa: boolean;
  hasOpenInspection: boolean;
  completedReports: MuellerAtHomeReportDetails[];
  forms: MuellerAtHomeForm[];
  appEnabled: boolean;
  webEnabled: boolean;
}

interface MuellerAtHomeStartResponse {
  error: boolean;
  errorMessage: string;
}

const blankInviteForm: MuellerAtHomeStartRequest = {
  phoneNumber: "",
  email: "",
  notes: "",
  includeCubiCasaRequest: false,
  automaticShareFormIds: [],
  requestInitiator: null,
  createWebLink: false,
};

interface MuellerAtHomeInviteProps {
  controlNumber: number;
  // Redux
  canSendMuellerAtHome: boolean;
  makeSuccessToast: (message: string) => void;
  makeErrorToast: (message: string) => void;
}

enum AtHomeExperienceType {
  APP = "APP",
  WEB = "WEB",
  NONE = "NONE",
}

const MuellerAtHomeInvite = (props: MuellerAtHomeInviteProps) => {
  const [status, setStatus] = useState<MuellerAtHomeFullStatus | undefined>(undefined);
  const [fetching, setFetching] = useState<boolean>(true);
  const [form, setForm] = useState<MuellerAtHomeStartRequest>(blankInviteForm);
  const [warningBypassed, setWarningBypassed] = useState<boolean>(false);
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [recipient, setRecipient] = useState<string>("");
  const [atHomeExperienceType, setAtHomeExperienceType] = useState<AtHomeExperienceType>(AtHomeExperienceType.NONE);

  const mounted = useRef<boolean>(true);

  useEffect(() => {
    getMuellerAtHomeStatus(
      props.controlNumber,
      (response: MuellerAtHomeFullStatus) => {
        setStatus(response);
        if (response.webEnabled && !response.appEnabled) {
          setForm({ ...form, createWebLink: true });
          setAtHomeExperienceType(AtHomeExperienceType.WEB);
        } else if (response.appEnabled && !response.webEnabled) {
          setAtHomeExperienceType(AtHomeExperienceType.APP);
        } // If both are enabled, the user can choose
        setFetching(false);
      },
      () => setFetching(false),
      mounted
    );
    return () => {
      mounted.current = false;
    };
  }, [props.controlNumber]);

  const toggleDialog = () => setShowDialog(!showDialog);
  const buttonEnabled = props.canSendMuellerAtHome && !fetching && status?.canOpenNewInspection;

  const send = () => {
    let request: MuellerAtHomeStartRequest = { ...form };

    if (rxPhoneNumber.test(recipient)) {
      request.phoneNumber = recipient;
      request.email = ""; // ensure only phone is sent
    } else if (rxEmailAddress.test(recipient)) {
      request.email = recipient;
      request.phoneNumber = ""; // ensure only email is sent
    } else {
      props.makeErrorToast("Invalid phone number or email address.");
      return;
    }

    request.createWebLink = atHomeExperienceType === AtHomeExperienceType.WEB;

    setFetching(true);

    const onSuccess = (result: MuellerAtHomeStartResponse) => {
      if (result.error) {
        setFetching(false);
        setForm({...form, phoneNumber: "", email: ""});
        props.makeErrorToast(result.errorMessage);
      } else {
        setForm(blankInviteForm);
        setStatus(null);
        setShowDialog(false);
        setFetching(false);
      }
    };
    const onError = () => {
      setFetching(false);
      setForm({...form, phoneNumber: "", email: ""});
      props.makeErrorToast("Error creating Mueller@Home inspection.");
    };

    startMuellerAtHome(props.controlNumber, request, onSuccess, onError, mounted);
  };

  const closeDialogButton = (
    <Button raised secondary onClick={toggleDialog}>
      Cancel
    </Button>
  );

  const warningDialogButtons = [
    closeDialogButton,
    <Button raised primary disabled={!status?.canOpenNewInspection} onClick={() => setWarningBypassed(true)}>
      Continue
    </Button>,
  ];

  const inviteDialogButtons = [
    closeDialogButton,
    <Button raised primary disabled={fetching || !status?.canOpenNewInspection} onClick={send}>
      Send
    </Button>,
  ];

  const showLoading = status === undefined || fetching;
  const showWarning =
    (!showLoading && !status?.canOpenNewInspection) || (status?.hasOpenInspection && !warningBypassed);
  const showInvite = !showLoading && !showWarning;

  return (
    <>
      <Button id={"action-mueller-at-home"} raised primary onClick={toggleDialog} disabled={!buttonEnabled}>
        Mueller@Home
      </Button>
      {/* Conditional MDialogs to work around a bug where the dialog cover was left in the dom */}
      {showDialog && showLoading && (
        <MDialog id="mueller-at-home-dialog-1" visible={showDialog && showLoading} title="Mueller@Home" width={400}>
          <MSpinner />
        </MDialog>
      )}
      {showDialog && showWarning && (
        <MDialog
          id="mueller-at-home-dialog-2"
          visible={showDialog && showWarning}
          contentActions={warningDialogButtons}
          title="Mueller@Home Warning"
          width={400}
        >
          {status?.hasOpenInspection && (
            <h2>
              There is already an open Mueller@Home inspection for this case. Opening another will cancel the
              in-progress inspection.
            </h2>
          )}
          {!status?.canOpenNewInspection && <h2>Mueller@Home is not active on this survey type.</h2>}
        </MDialog>
      )}
      {showDialog && showInvite && (
        <MDialog
          id="mueller-at-home-dialog-3"
          visible={showDialog && showInvite}
          className="mueller-at-home-dialog"
          contentActions={inviteDialogButtons}
          title="Mueller@Home"
          width={400}
        >
          {status?.completedReports.length === 0 || (
            <table>
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Fulfilled/Requested</th>
                </tr>
              </thead>
              <tbody>
                {status?.completedReports.map((h) => (
                  <tr key={h.createdDate}>
                    <td>{h.status}</td>
                    <td>
                      {h.completedDate !== null ? dateToStringYy(h.completedDate) : dateToStringYy(h.createdDate)}
                    </td>
                    <td>{h.completedDate !== null ? `${h.fulfilledRequestCount}/${h.requestCount}` : "(none)"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {status?.appEnabled && status?.webEnabled && (
            <>
              <label>This account is configured for 2 Mueller@Home experiences:</label>
              <FormControl component="fieldset">
                <RadioGroup
                  aria-label="mueller at home experience type"
                  name="atHomeExperienceType"
                  value={atHomeExperienceType}
                  onChange={(e) => setAtHomeExperienceType(e.target.value as AtHomeExperienceType)}
                >
                  <FormControlLabel
                    value={AtHomeExperienceType.APP}
                    control={<Radio />}
                    label="App-based including Cubicasa and shared photos"
                  />
                  <FormControlLabel
                    value={AtHomeExperienceType.WEB}
                    control={<Radio />}
                    label="Web-based using forms only"
                  />
                </RadioGroup>
              </FormControl>
            </>
          )}
          {status?.webEnabled && atHomeExperienceType === AtHomeExperienceType.WEB && (
            <>
              <label>The web-based experience will include these forms:</label>
              <ul>
                {status?.forms.map((f) => (
                  <li>{f.name}</li>
                ))}
              </ul>
            </>
          )}
          {status?.appEnabled && atHomeExperienceType === AtHomeExperienceType.APP && (
            <>
              <Checkbox
                id={"mueller-at-home-select-all"}
                name={"mueller-at-home-select-all"}
                label={"Select All"}
                checked={form.includeCubiCasaRequest && form.automaticShareFormIds.length == status.forms.length}
                onChange={(checked) => {
                  setForm((prevState) => ({
                    ...prevState,
                    includeCubiCasaRequest: checked,
                    automaticShareFormIds: checked ? [...status.forms.map((f) => f.id)] : [],
                  }));
                }}
              />
              {status?.canOfferCubiCasa && (
                <Checkbox
                  id={"mueller-at-home-cubi"}
                  name={"mueller-at-home-cubi"}
                  label={"CubiCasa"}
                  checked={form.includeCubiCasaRequest}
                  onChange={(checked) => {
                    setForm((prevState) => ({ ...prevState, includeCubiCasaRequest: checked }));
                  }}
                />
              )}
              {status?.forms.map((f) => (
                <Checkbox
                  key={f.id}
                  id={f.id}
                  name={f.id}
                  label={f.name}
                  checked={form.automaticShareFormIds.some((id) => id === f.id)}
                  onChange={(checked) => {
                    setForm((prevState) => ({
                      ...prevState,
                      automaticShareFormIds: checked
                        ? [...prevState.automaticShareFormIds, f.id]
                        : [...prevState.automaticShareFormIds.filter((id) => id !== f.id)],
                    }));
                  }}
                />
              ))}
            </>
          )}
          <TextField
            label={"Send To:"}
            placeholder={"Phone or Email"}
            value={recipient}
            onChange={(value) => {
              setRecipient(`${value}`);
            }}
          />
        </MDialog>
      )}
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    canSendMuellerAtHome: userHasRight(SEND_MUELLER_HOME_LINK)(state),
  };
};

const mapDispatchToProps = (dispatch) => ({
  makeSuccessToast(text: string) {
    responseHandler.customToast(text, "success", dispatch);
  },
  makeErrorToast(text: string) {
    responseHandler.customToast(text, "error", dispatch);
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(MuellerAtHomeInvite);
